import { useForm } from "react-hook-form";

import { Form } from "@/components/Form/Form";
import { FormInput } from "@/components/Form/FormInput";
import { AlertTriangleIcon } from "@/components/Icons/AlertTriangleIcon";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import { Notice } from "@/design-system/Notice";
import { Stack } from "@/design-system/Stack";
import { createRequiredStringRule } from "@/helpers/rules";
import { useTranslation } from "@/translations";

const FORM_ID = "slug-modal-form";

type SlugModalProps = ModalBaseProps & {
  initialSlug: string;
  takenSlugs: string[];
  onSubmit: (slug: string) => void;
};

export function SlugModal({
  isOpened,
  onOpenChange,
  initialSlug,
  takenSlugs,
  onSubmit,
}: SlugModalProps): React.ReactNode {
  const { t } = useTranslation();
  const form = useForm<SlugModalValues>({
    defaultValues: {
      slug: initialSlug,
    },
  });

  return (
    <Modal.Root size="sm" title={t("page.projects.slug-modal.title")} {...{ isOpened, onOpenChange }}>
      <Modal.Body>
        <Form.Root<SlugModalValues>
          formMethods={form}
          id={FORM_ID}
          onSubmit={(data) => onSubmit(data.slug.trim().toLowerCase())}
          noPrompt
        >
          <Stack gap="md" align="center">
            <Notice icon={<AlertTriangleIcon />} message={t("page.projects.slug-modal.warning")} variant="warning" />
            <FormInput<SlugModalValues>
              id="slug"
              name="slug"
              rules={{
                maxLength: {
                  message: t("components.form.error.max-length", {
                    length: 64,
                  }),
                  value: 64,
                },
                validate: {
                  required: createRequiredStringRule(t, "page.projects.form.slug"),
                  mustNotContainSpecialCharacters: (value) => {
                    if (!value) {
                      return undefined;
                    }

                    return /[^A-Za-z0-9-]/.test(value) ? t("components.form.error.no-special-characters") : undefined;
                  },
                  mustBeUnique: (value) => {
                    if (!value) {
                      return undefined;
                    }

                    if (value === initialSlug) {
                      return undefined;
                    }

                    return takenSlugs.includes(value) ? t("page.projects.slug-modal.error.not-unique") : undefined;
                  },
                },
              }}
            />
          </Stack>
        </Form.Root>
      </Modal.Body>
      <Modal.Actions>
        <Modal.Close>
          <Button type="button" variant="outline">
            {t("page.projects.slug-modal.cancel")}
          </Button>
        </Modal.Close>
        <Button type="submit" form={FORM_ID}>
          {t("page.projects.slug-modal.submit")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}

interface SlugModalValues {
  slug: string;
}
